import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { formatCZK } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { day, todayIso, plusDays, PAID_PLANS, CYCLE_MONTHS, recommendPlan } from "@/components/superadmin/firm";

// 325: cesta zákazníka — z dema na klienta s první fakturou, která už je vystavená (vybraná ve Fakturoidu,
// nebo zadaná ručně). Do splatnosti plný přístup, den po splatnosti upozornění majiteli, za 3 dny zámek;
// platba (Fakturoid ji spáruje sám) nastaví „zaplaceno do“ na konec prvního období.

const CYCLES = [["month", "Měsíčně"], ["half", "Pololetně"], ["year", "Ročně"]];
const addMonths = (iso, n) => {
  const [y, m, d] = iso.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1 + n, 1));
  const last = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth() + 1, 0)).getUTCDate();
  t.setUTCDate(Math.min(d, last));
  return t.toISOString().slice(0, 10);
};
const FA_STATUS = { open: "neodeslaná", sent: "odeslaná", overdue: "po splatnosti", paid: "zaplacená" };

function Pills({ value, onChange, options, testid }) {
  return (
    <div className="flex gap-1 p-1 rounded-full bg-secondary w-fit max-w-full overflow-x-auto" role="radiogroup" data-testid={testid}>
      {options.map(([k, l]) => (
        <button key={k} type="button" role="radio" aria-checked={value === k} onClick={() => onChange(k)} data-testid={`${testid}-${k}`}
          className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${value === k ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
      ))}
    </div>
  );
}

export function FirstInvoiceDialog({ open, onOpenChange, c, plans, faConfigured, onDone }) {
  const [f, setF] = useState({});
  const [fa, setFa] = useState({ loading: false, invoices: [], candidates: [], error: null });
  const [busy, setBusy] = useState(false);
  const loadFa = async (subjectId) => {
    setFa({ loading: true, invoices: [], candidates: [], error: null });
    try {
      const { data } = await api.get(`/admin/companies/${c.id}/billing/fakturoid-invoices`, { params: subjectId ? { subject_id: subjectId } : {} });
      const inv = (data.invoices || []).filter((i) => !i.system && i.status !== "cancelled").reverse();
      setFa({ loading: false, invoices: inv, candidates: data.candidates || [], subject: data.subject, error: null });
      const pick = inv.find((i) => i.status !== "paid") || inv[0];
      setF((x) => (x.fid || !pick ? x : { ...x, fid: pick.id, start: x.startTouched ? x.start : (pick.issued_on || x.start) }));
    } catch (e) { setFa({ loading: false, invoices: [], candidates: [], error: formatApiError(e.response?.data?.detail) }); }
  };
  useEffect(() => {
    if (!open) return;
    const plan = (c.plan && c.plan !== "demo") ? c.plan : recommendPlan(c);
    setF({ plan, cycle: c.billing?.cycle || "month", start: String(c.paid_since || "").slice(0, 10) || todayIso(),
      startTouched: !!c.paid_since, source: faConfigured ? "fa" : "manual", fid: null, invoice_no: "", due_on: plusDays(todayIso(), 7), amount: "" });
    if (faConfigured) loadFa();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const price = plans?.[f.plan]?.price || 0;
  const months = CYCLE_MONTHS[f.cycle || "month"] || 1;
  const picked = f.source === "fa" ? fa.invoices.find((i) => i.id === f.fid) : null;
  const due = f.source === "fa" ? picked?.due_on : f.due_on;
  const until = f.start ? plusDays(addMonths(f.start, months), -1) : null;
  const ok = f.plan && f.start && (f.source === "fa" ? !!picked : (f.invoice_no || "").trim() && f.due_on);

  const submit = async () => {
    setBusy(true);
    try {
      const body = { plan: f.plan, billing_cycle: f.cycle, start: f.start };
      if (f.source === "fa") body.fakturoid_invoice_id = f.fid;
      else Object.assign(body, { invoice_no: f.invoice_no.trim(), due_on: f.due_on, amount: f.amount === "" ? null : Number(f.amount) });
      await api.post(`/admin/companies/${c.id}/billing/first-invoice`, body);
      onOpenChange(false);
      await onDone?.(picked?.status === "paid" ? "Klient · faktura už je zaplacená" : "Klient · čeká na první platbu");
    } catch (e) { onDone?.(null, formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90dvh] overflow-y-auto" data-testid="first-invoice-dialog">
        <DialogHeader>
          <DialogTitle>Převést na klienta</DialogTitle>
          <DialogDescription>{c.name} · první faktura je už vystavená</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <div className="min-w-0">
              <Label className="text-xs">Tarif</Label>
              <Select value={f.plan || ""} onValueChange={(v) => setF({ ...f, plan: v })}>
                <SelectTrigger className="h-11 mt-1 bg-card" data-testid="fi-plan" aria-label="Tarif"><SelectValue placeholder="Vyber tarif" /></SelectTrigger>
                <SelectContent>{PAID_PLANS.map(([k, l]) => <SelectItem key={k} value={k}>{plans?.[k] ? `${l} · ${plans[k].price.toLocaleString("cs-CZ")} Kč/měs` : l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="min-w-0">
              <Label className="text-xs" htmlFor="fi-start">Spolupráce od</Label>
              <Input id="fi-start" type="date" value={f.start || ""} onChange={(e) => setF({ ...f, start: e.target.value, startTouched: true })} className="h-11 mt-1 w-full min-w-0" data-testid="fi-start" />
            </div>
          </div>
          <div>
            <div className="text-xs font-medium mb-1">Období</div>
            <div className="flex flex-wrap items-center gap-2">
              <Pills value={f.cycle} onChange={(v) => setF({ ...f, cycle: v })} options={CYCLES} testid="fi-cycle" />
              <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">{formatCZK(price * months)} za období</span>
            </div>
          </div>

          <div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
              <span className="text-sm font-bold">První faktura</span>
              <Pills value={f.source} onChange={(v) => setF({ ...f, source: v })} testid="fi-source"
                options={faConfigured ? [["fa", "Z Fakturoidu"], ["manual", "Ručně"]] : [["manual", "Ručně"]]} />
            </div>
            {f.source === "fa" ? (
              <div className="rounded-[12px] border border-border divide-y divide-border" data-testid="fi-fa-list">
                {fa.loading && <div className="p-3 text-sm text-muted-foreground flex items-center gap-2"><Loader2 size={15} className="animate-spin motion-reduce:animate-none" /> Načítám faktury z Fakturoidu…</div>}
                {fa.error && <div className="p-3 text-sm text-rose-600 dark:text-rose-400">{fa.error}</div>}
                {!fa.loading && fa.candidates.length > 0 && (
                  <div className="p-3 space-y-2">
                    <div className="text-xs text-muted-foreground">Ve Fakturoidu je víc kontaktů — vyber firmu:</div>
                    <div className="flex flex-wrap gap-2">{fa.candidates.map((s) => (
                      <Button key={s.id} type="button" size="sm" variant="outline" className="max-w-full" onClick={() => loadFa(s.id)}><span className="truncate">{s.name}{s.registration_no ? ` · ${s.registration_no}` : ""}</span></Button>))}</div>
                  </div>
                )}
                {!fa.loading && !fa.error && !fa.candidates.length && !fa.invoices.length && (
                  <div className="p-3 text-sm text-muted-foreground">Ve Fakturoidu u téhle firmy žádnou fakturu nevidím — zadej ji ručně.</div>
                )}
                {fa.invoices.map((i) => (
                  <label key={i.id} className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer min-h-[52px] ${f.fid === i.id ? "bg-brand/5" : "hover:bg-secondary/60"}`} data-testid={`fi-inv-${i.id}`}>
                    <input type="radio" name="fi-inv" checked={f.fid === i.id} onChange={() => setF({ ...f, fid: i.id, start: f.startTouched ? f.start : (i.issued_on || f.start) })} className="accent-[hsl(var(--brand))] shrink-0" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold truncate">č. {i.number} · {formatCZK(i.total)}</span>
                      <span className="block text-xs text-muted-foreground">vystavena {day(i.issued_on)} · splatnost {day(i.due_on)} · {FA_STATUS[i.status] || i.status}</span>
                    </span>
                  </label>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5" data-testid="fi-manual">
                <div className="min-w-0"><Label className="text-xs" htmlFor="fi-no">Číslo faktury</Label>
                  <Input id="fi-no" value={f.invoice_no || ""} onChange={(e) => setF({ ...f, invoice_no: e.target.value })} placeholder="2026-0101" className="h-11 mt-1 w-full min-w-0" data-testid="fi-no" /></div>
                <div className="min-w-0"><Label className="text-xs" htmlFor="fi-due">Splatnost</Label>
                  <Input id="fi-due" type="date" value={f.due_on || ""} onChange={(e) => setF({ ...f, due_on: e.target.value })} className="h-11 mt-1 w-full min-w-0" data-testid="fi-due" /></div>
                <div className="min-w-0"><Label className="text-xs" htmlFor="fi-amount">Částka (Kč)</Label>
                  <Input id="fi-amount" type="number" inputMode="decimal" value={f.amount ?? ""} placeholder={String(price * months)} onChange={(e) => setF({ ...f, amount: e.target.value })} className="h-11 mt-1 w-full min-w-0 tabular-nums" data-testid="fi-amount" /></div>
              </div>
            )}
          </div>

          {f.start && (
            <div className="rounded-[12px] bg-secondary px-3.5 py-3 text-sm space-y-1" data-testid="fi-preview">
              {picked?.status === "paid" ? (
                <div>Faktura je už zaplacená → <b className="text-success">zaplaceno do {day(until)}</b>, dál běží obnovy.</div>
              ) : (
                <>
                  <div>Do <b>{due ? day(due) : "splatnosti"}</b> plný přístup, stav „Čeká na první platbu“.</div>
                  {due && <div>Když nezaplatí: <b>{day(plusDays(due, 1))}</b> upozornění majiteli, <b>{day(plusDays(due, 4))}</b> zámek.</div>}
                  <div>Po zaplacení zaplaceno do <b className="text-success">{day(until)}</b>{faConfigured && f.source === "fa" ? " — platbu Fakturoid spáruje sám" : ""}.</div>
                </>
              )}
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-1">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto">Zrušit</Button>
            <Button disabled={busy || !ok} onClick={submit} className="w-full sm:w-auto font-bold" data-testid="fi-submit">Převést na klienta</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
